<script lang="ts">
  // Floating bar for actions on a selection (registrations, client entries).
  //   {#if selected.length}
  //     <BulkBar count={selected.length} summary="12:30 · € 1.240" onclear={clearSelection}>
  //       {#snippet actions()}<Button size="sm" variant="success">…</Button>{/snippet}
  //     </BulkBar>
  //   {/if}
  // Sticks to the bottom of the scroll area, above the mobile tab bar. On
  // phones the actions take the full width.
  import type { Snippet } from 'svelte';
  import { X } from '@lucide/svelte';
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props {
    count: number;
    /** Line under the count (totals of the selection). */
    summary?: string | Snippet;
    onclear: () => void;
    actions: Snippet;
    /** Accessible name of the region. */
    label?: string;
    /** sticky (default, inside the page flow) or fixed to the viewport. */
    position?: 'sticky' | 'fixed';
  }
  let { count, summary, onclear, actions, label, position = 'sticky' }: Props = $props();
</script>

<div class="bulk {position}" role="region" aria-label={label || t('ui.bulk_label')}>
  <div class="info">
    <button type="button" class="x" aria-label={t('ui.clear_selection')} onclick={onclear}><X /></button>
    <div class="stats">
      <strong aria-live="polite">{t('ui.n_selected', { count })}</strong>
      {#if summary}
        <span class="summary tabular">{#if typeof summary === 'string'}{summary}{:else}{@render summary()}{/if}</span>
      {/if}
    </div>
  </div>
  <div class="actions">{@render actions()}</div>
</div>

<style>
  .bulk {
    bottom: var(--space-4);
    z-index: var(--z-floating);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    flex-wrap: wrap;
    margin-top: var(--space-4);
    padding: var(--space-2_5) var(--space-3);
    background: var(--bg-elevated);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    animation: rise-in 180ms var(--ease);
  }
  .sticky { position: sticky; }
  .fixed { position: fixed; left: var(--space-4); right: var(--space-4); max-width: 720px; margin: 0 auto; }
  .info { display: flex; align-items: center; gap: var(--space-2_5); min-width: 0; }
  .x {
    width: 28px;
    height: 28px;
    border-radius: var(--radius-full);
    border: 0;
    background: var(--surface-3);
    color: var(--text-2);
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .x :global(svg) { width: var(--icon-sm); height: var(--icon-sm); }
  .x:hover { color: var(--text); }
  .x:focus-visible { outline: none; box-shadow: var(--ring); }
  .stats { display: flex; flex-direction: column; font-size: var(--text-sm); color: var(--text-2); min-width: 0; }
  .stats strong { color: var(--text); font-size: var(--text-base); }
  .summary { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .actions { display: flex; align-items: center; gap: var(--space-1_5); flex-wrap: wrap; }
  @media (max-width: 899px) {
    .bulk { bottom: calc(var(--mobile-nav-h) + var(--space-3) + env(safe-area-inset-bottom)); }
  }
  @media (max-width: 759px) {
    .bulk { padding: var(--space-2) var(--space-2_5); }
    .actions { width: 100%; }
    .actions :global(.btn) { flex: 1; }
  }
</style>
